import React, {useState} from "react";

import styles from "./KnowledgeRecordsTab.module.css";
import {KnowledgeRecordAttributeUi} from "../assistant/franchiseTypes";
import {KNOWLEDGE_ATTRIBUTE_VALUE_MAX_LENGTH, KnowledgeAttributeDefinitionUi} from "./knowledgeDefinitionsApi";
import {CustomInputField} from "../../elements/customInputField/CustomInputField";
import {SelectDropdown, SelectOption} from "../../elements/select/SelectDropdown";

export const ATTRIBUTE_VALUE_REQUIRED_MESSAGE = "Value is required";

/** A record cannot be saved while any of its attributes has no value. */
export const hasBlankAttributeValue = (attributes: KnowledgeRecordAttributeUi[]): boolean =>
    attributes.some(attribute => !attribute.value.trim());

export const getAttributeName = (attribute: KnowledgeRecordAttributeUi,
                                 definitions: KnowledgeAttributeDefinitionUi[]): string =>
    definitions.find(definition => definition.key === attribute.key)?.name || attribute.name || attribute.key;

interface Props {
    attributes: KnowledgeRecordAttributeUi[];
    definitions: KnowledgeAttributeDefinitionUi[];
    onChange: (attributes: KnowledgeRecordAttributeUi[]) => void;
}

/**
 * The attributes of one knowledge record being edited: pick a defined attribute that is not on the
 * record yet, then give it a value. Values other records already use are suggested: Aime matches
 * them exactly, and the platform keeps one spelling per value regardless of case. Every added
 * attribute needs a value, so the record's Save stays disabled until it is filled or removed.
 */
export const KnowledgeRecordAttributesField: React.FC<Props> = ({attributes, definitions, onChange}) => {
    const [editedKeys, setEditedKeys] = useState<Set<string>>(new Set());
    const usedKeys = new Set(attributes.map(attribute => attribute.key));
    const availableOptions: SelectOption<string>[] = definitions
        .filter(definition => !usedKeys.has(definition.key))
        .map(definition => ({label: definition.name, value: definition.key}));

    const addAttribute = (key?: string) => {
        const definition = definitions.find(item => item.key === key);
        if (!definition || usedKeys.has(definition.key)) {
            return;
        }
        onChange([...attributes, {key: definition.key, name: definition.name, value: ""}]);
    };

    const updateValue = (key: string, value: string) => {
        setEditedKeys(current => new Set(current).add(key));
        onChange(attributes.map(attribute => attribute.key === key ? {...attribute, value} : attribute));
    };

    const removeAttribute = (key: string) => {
        setEditedKeys(current => {
            const next = new Set(current);
            next.delete(key);
            return next;
        });
        onChange(attributes.filter(attribute => attribute.key !== key));
    };

    return <div className={styles.attributesField}>
        <div className={styles.attributesHeader}>
            <span className={styles.attributesTitle}>Attributes</span>
            <span className={styles.attributesDescription}>
                Optional. Aime uses an attribute's value to pick this knowledge only when it is relevant.
            </span>
        </div>
        {attributes.map(attribute => {
            const name = getAttributeName(attribute, definitions);
            const suggestions = definitions.find(definition => definition.key === attribute.key)?.values;
            return <div className={styles.attributeRow} key={attribute.key}>
                <div className={styles.attributeValue}>
                    <CustomInputField
                        isRequired={true}
                        title={name}
                        value={attribute.value}
                        placeholder={"Value"}
                        maxLength={KNOWLEDGE_ATTRIBUTE_VALUE_MAX_LENGTH}
                        suggestions={suggestions}
                        setValue={value => updateValue(attribute.key, value)}
                        errorMessage={editedKeys.has(attribute.key) && !attribute.value.trim()
                            ? ATTRIBUTE_VALUE_REQUIRED_MESSAGE
                            : undefined}
                    />
                </div>
                <button
                    type={"button"}
                    className={styles.attributeRemoveButton}
                    onClick={() => removeAttribute(attribute.key)}
                    title={`Remove ${name}`}
                    aria-label={`Remove ${name}`}
                >
                    <img src={"/deleteIcon.svg"} alt={""}/>
                </button>
            </div>
        })}
        {definitions.length === 0
            ? <span className={styles.attributesDescription}>
                No attributes are defined yet. Add them on the Knowledge settings page.
            </span>
            : <div className={styles.selectField}>
                <SelectDropdown<string>
                    // Remounted whenever the record's attributes change, so the picker always
                    // returns to its placeholder instead of showing the attribute just added.
                    key={attributes.map(attribute => attribute.key).join(",")}
                    title={"Add attribute"}
                    value={undefined}
                    handleChange={addAttribute}
                    options={availableOptions}
                    selectFirstOptionByDefault={false}
                    disabled={availableOptions.length === 0}
                    placeholder={availableOptions.length ? "Select an attribute" : "All attributes added"}
                    // The record card clips its overflow and its Save and Cancel buttons sit above the
                    // field, so the option list is drawn on top of the page to show every attribute.
                    renderMenuOnTop={true}
                />
            </div>
        }
    </div>
}
